/**
 * Integrytas Group - Common Security Module (CSM)
 * Page: CSM.HIERARCHY.LIST
 *
 * Maintain reusable approval hierarchies. A job description references one
 * hierarchy by ID, and many jobs can share the same chain.
 *
 *  - Default: exactly one hierarchy may be `is_default='Y'`. The DB trigger
 *    `csm_enforce_single_default_hierarchy` clears the previous default
 *    automatically when another row is set as default, so the UI just
 *    toggles the checkbox.
 *  - The approver chain is drag-and-drop reorderable inside the edit drawer.
 */

import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useActiveLocale } from '../../lib/use_active_locale';
import { buildTranslationsGroup } from '../../components/common/translations_group';
import { apiDb } from '../../lib/api_client';
import { routedInsert, routedUpdate, routedDelete, routedInsertMany } from '../../lib/routed_writes';
import type { AuthSession } from '../../types/auth';
import { FilteredModalSingleSelect } from '../../components/common/FilteredModalSingleSelect';
import { SortableHeader } from '../../components/common/SortableHeader';
import { YesNoBadge } from '../../components/common/YesNoBadge';
import { compareStrings, useSort } from '../../lib/use_sort';
import { PersonalizationProvider, PersonalizationToggle, PersonalizedGrid, PersonalizedField, GRID_TABLE_CLASS } from '../../components/personalization';
import { usePersonalization } from '../../lib/use_personalization';
import type { GridColumnDef } from '../../components/personalization';
import { Modal } from '../../components/common/Modal';
import { ConfirmModal } from '../../components/common/ConfirmModal';
import { EmptyState } from '../../components/common/EmptyState';
import { ErrorBanner } from '../../components/common/ErrorBanner';
import { useDirtyForm } from '../../hooks/use_dirty_form';
import { FieldRenderer, toUldCoreLayout } from '../../components/common/FieldRenderer';
import type { FieldDef } from '../../components/common/FieldRenderer';
import { useUldLayout } from '../../hooks/use_uld_layout';
import { useUdcFormFields } from '../../hooks/use_udc_form_fields';
import { UdcFacets } from '../../components/common/UdcFacets';
import { FacetFilter } from '../../components/common/FacetFilter';
import { ClearFiltersButton } from '../../components/common/ClearFiltersButton';
import { RelatedChildrenPanel, RelatedExpandButton, useExpandedRows, type RelatedChildGroup } from '../../components/common/RelatedChildrenPanel';
import { PageHeader } from '../../components/common/PageHeader';
import { ContentCard } from '../../components/common/ContentCard';
import { Search, Plus, RefreshCw } from 'lucide-react';
import { useUdcGrid } from '../../hooks/use_udc_grid';
import { registerUdcHost } from '../../lib/udc_hosts';
import { hasAccess } from '../../types/auth';

const PAGE_CODE = 'CSM.HIERARCHY.LIST';

registerUdcHost('CSM.HIERARCHY.LIST', 'csm_approval_hierarchy', 'Approval Hierarchies');

type HierarchySortKey = 'internal_code' | 'display_text' | 'is_default' | 'is_active' | 'step_count';

const HIER_FIELD_DEFS: FieldDef[] = [
  { key: 'internal_code', type: 'text',    label: 'Internal Code', required: true },
  { key: 'is_active',     type: 'boolean', label: 'Active' },
  { key: 'is_default',    type: 'boolean', label: 'Default' },
];
const HIER_CORE_LAYOUT = toUldCoreLayout(HIER_FIELD_DEFS);

interface HierarchyRow {
  approval_hierarchy_id: number;
  internal_code: string;
  is_default: 'Y' | 'N';
  is_active: 'Y' | 'N';
  requires_manager_approval: 'Y' | 'N';
  display_text: string;
  step_count: number;
  /** Every locale's text, kept so the related panel can list them (§17.B14). */
  tl: { locale_code: string; display_text: string }[];
}

interface StepRow {
  approval_hierarchy_step_id: number;
  user_id: number;
  user_label: string;
  sequence_order: number;
  is_active: 'Y' | 'N';
}

interface UserOpt {
  user_id: number;
  display_name: string;
  email: string;
}

interface TLRow {
  locale_code: string;
  display_text: string;
  description: string;
}

// Related-children (expand/contract detail panel), keyed by approval_hierarchy_id.
interface StepChild {
  step_id: number;
  label: string;          // resolved approver display name (never a raw id) §17.B4
  email: string;          // hover title
  sequence_order: number; // rendered as a badge
}
interface JobChild {
  job_id: number;
  name: string;           // resolved job description name
  code: string;           // internal_code, hover title
}

const FILTER_KEY = 'csm.hierarchies.filter';
const SORT_STEP  = 10;

const GRID_COLS: GridColumnDef[] = [
  { key: 'internal_code', header: 'Code',      defaultWidth: 160 },
  { key: 'display_text',  header: 'Name',      defaultWidth: 200 },
  { key: 'step_count',    header: 'Approvers', defaultWidth: 90  },
  { key: 'is_default',    header: 'Default',   defaultWidth: 90  },
  { key: 'is_active',     header: 'Active',    defaultWidth: 80  },
];

export interface ApprovalHierarchyPageProps {
  session: AuthSession;
}

export function ApprovalHierarchyPage({ session }: ApprovalHierarchyPageProps) {
  return (
    <PersonalizationProvider pageCode="CSM.HIERARCHY.LIST" session={session}>
      <ApprovalHierarchyPageInner session={session} />
    </PersonalizationProvider>
  );
}

function ApprovalHierarchyPageInner({ session }: ApprovalHierarchyPageProps) {
  // §6 RBAC: csm_role_page_access decides which write affordances this user
  // gets. The server refuses the write regardless (RLS via the Common
  // Routine); this stops the page offering an action that can only error.
  const canAdd = hasAccess(session, PAGE_CODE, 'add');
  const canChange = hasAccess(session, PAGE_CODE, 'change');
  const canDelete = hasAccess(session, PAGE_CODE, 'delete');

  const { t } = useTranslation();
  // §8: resolve _tl data against the ACTIVE locale (follows a runtime switch), not
  // the stored preferred_locale (stale in memory until re-login).
  const activeLocale = useActiveLocale();
  const { isPersonalizing } = usePersonalization();
  const [rows, setRows]       = useState<HierarchyRow[]>([]);
  // Related-children (expand/contract detail panel), keyed by approval_hierarchy_id.
  const [stepsByHier, setStepsByHier] = useState<Map<number, StepChild[]>>(new Map());
  const [jobsByHier, setJobsByHier]   = useState<Map<number, JobChild[]>>(new Map());
  const detail = useExpandedRows<number>();
  const [filter, setFilter]   = useState(() => sessionStorage.getItem(FILTER_KEY) ?? '');
  const [editing, setEditing] = useState<HierarchyRow | null>(null);
  const [creating, setCreating] = useState(false);
  const [busy, setBusy]   = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<HierarchyRow | null>(null);
  // §17.B5: per-column multi-value facets (logical columns of the list).
  const [facets, setFacets] = useState<{ isDefault: string[]; active: string[] }>({ isDefault: [], active: ['Y'] });
  const sort = useSort<HierarchySortKey>('csm.hierarchies.sort', { key: 'internal_code', dir: 'asc' });

  useEffect(() => { sessionStorage.setItem(FILTER_KEY, filter); }, [filter]);

  const reload = async () => {
    setBusy(true); setError(null);
    try {
      const locale = activeLocale;
      const { data, error: e } = await apiDb
        .from('csm_approval_hierarchy')
        .select(`
          approval_hierarchy_id, internal_code, is_default, is_active,
          requires_manager_approval,
          csm_approval_hierarchy_tl ( locale_code, display_text ),
          csm_approval_hierarchy_step ( approval_hierarchy_step_id, is_active )
        `)
        .order('internal_code');
      if (e) throw e;
      type RawHier = {
        approval_hierarchy_id: number;
        internal_code: string;
        is_default: 'Y' | 'N';
        is_active: 'Y' | 'N';
        requires_manager_approval?: 'Y' | 'N';
        csm_approval_hierarchy_tl?: { locale_code: string; display_text: string }[];
        csm_approval_hierarchy_step?: { approval_hierarchy_step_id: number; is_active: 'Y' | 'N' }[];
      };
      const list: HierarchyRow[] = (data ?? []).map((h: RawHier) => {
        const tls: { locale_code: string; display_text: string }[] = h.csm_approval_hierarchy_tl ?? [];
        const exact = tls.find(t => t.locale_code === locale);
        const fb    = tls.find(t => t.locale_code === 'EN_US') ?? tls[0];
        const steps = h.csm_approval_hierarchy_step ?? [];
        return {
          approval_hierarchy_id: h.approval_hierarchy_id,
          internal_code: h.internal_code,
          is_default: h.is_default,
          is_active: h.is_active,
          requires_manager_approval: h.requires_manager_approval ?? 'N',
          display_text: exact?.display_text ?? fb?.display_text ?? h.internal_code,
          step_count: steps.filter((s) => s.is_active === 'Y').length,
          tl: tls,
        };
      });
      setRows(list);

      // ── Related children for the expand/contract detail panel ──────────────
      // Approval steps (approvers, by sequence) + the job descriptions that
      // reference each hierarchy. Every display value is resolved (§17.B4).
      const [stepRes, jobRes] = await Promise.all([
        apiDb.from('csm_approval_hierarchy_step').select(`
          approval_hierarchy_step_id, approval_hierarchy_id, user_id, sequence_order,
          csm_user_master:user_id ( display_name, email )
        `).order('sequence_order'),
        apiDb.from('csm_job_description').select(`
          job_description_id, approval_hierarchy_id, internal_code,
          csm_job_description_tl ( locale_code, display_text )
        `),
      ]);
      if (stepRes.error) throw stepRes.error;
      if (jobRes.error) throw jobRes.error;

      type RawStepChild = {
        approval_hierarchy_step_id: number;
        approval_hierarchy_id: number;
        user_id: number;
        sequence_order: number;
        csm_user_master?: { display_name?: string; email?: string } | null;
      };
      const stepMap = new Map<number, StepChild[]>();
      for (const s of (stepRes.data ?? []) as RawStepChild[]) {
        const arr = stepMap.get(s.approval_hierarchy_id) ?? [];
        arr.push({
          step_id: s.approval_hierarchy_step_id,
          label: s.csm_user_master?.display_name ?? s.csm_user_master?.email ?? `#${s.user_id}`,
          email: s.csm_user_master?.email ?? '',
          sequence_order: s.sequence_order,
        });
        stepMap.set(s.approval_hierarchy_id, arr);
      }

      type RawJobChild = {
        job_description_id: number;
        approval_hierarchy_id: number | null;
        internal_code: string;
        csm_job_description_tl?: { locale_code: string; display_text: string }[];
      };
      const jobMap = new Map<number, JobChild[]>();
      for (const j of (jobRes.data ?? []) as RawJobChild[]) {
        if (j.approval_hierarchy_id == null) continue;
        const tls = j.csm_job_description_tl ?? [];
        const name = tls.find(x => x.locale_code === locale)?.display_text
          ?? tls.find(x => x.locale_code === 'EN_US')?.display_text
          ?? tls[0]?.display_text ?? j.internal_code;
        const arr = jobMap.get(j.approval_hierarchy_id) ?? [];
        arr.push({ job_id: j.job_description_id, name, code: j.internal_code });
        jobMap.set(j.approval_hierarchy_id, arr);
      }
      setStepsByHier(stepMap);
      setJobsByHier(jobMap);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  // reload is re-created each render; re-resolve _tl display on locale switch.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { void reload();   }, [activeLocale]);

  const handleDelete = async (h: HierarchyRow): Promise<boolean> => {
    setBusy(true); setError(null);
    try {
      if (h.is_default === 'Y') {
        throw new Error(t('approval_hierarchy.list.errors.cannot_delete_default'));
      }
      const { count: jobCount, error: jErr } = await apiDb
        .from('csm_job_description')
        .select('job_description_id', { head: true, count: 'exact' })
        .eq('approval_hierarchy_id', h.approval_hierarchy_id);
      if (jErr) throw jErr;
      if ((jobCount ?? 0) > 0) {
        throw new Error(
          jobCount === 1
            ? t('approval_hierarchy.list.errors.jobs_reference_one', { count: jobCount ?? undefined })
            : t('approval_hierarchy.list.errors.jobs_reference_other', { count: jobCount ?? undefined }),
        );
      }
      const { count: reqCount, error: rErr } = await apiDb
        .from('csm_user_provision_request')
        .select('user_provision_request_id', { head: true, count: 'exact' })
        .eq('target_approval_hierarchy_id', h.approval_hierarchy_id);
      if (rErr) throw rErr;
      if ((reqCount ?? 0) > 0) {
        throw new Error(
          reqCount === 1
            ? t('approval_hierarchy.list.errors.requests_reference_one', { count: reqCount ?? undefined })
            : t('approval_hierarchy.list.errors.requests_reference_other', { count: reqCount ?? undefined }),
        );
      }
      const { error: dErr } = await routedDelete(session, 'csm_approval_hierarchy', 'approval_hierarchy_id', h.approval_hierarchy_id);
      if (dErr) throw dErr;
      await reload();
      return true;
    } catch (e) {
      setError((e as Error).message);
      return false;
    } finally {
      setBusy(false);
    }
  };

  // User-defined columns / facets an admin placed on this page (runtime, no code).
  const udcGrid = useUdcGrid('csm_approval_hierarchy', 'CSM.HIERARCHY.LIST', { pkColumn: 'approval_hierarchy_id', rows: rows as unknown as Record<string, unknown>[] });

  // Distinct facet options derived from the loaded rows (§17.B5).
  const facetOptions = useMemo(() => ({
    isDefault: [{ value: 'Y', label: t('approval_hierarchy.list.facet.default', 'Default') }, { value: 'N', label: t('approval_hierarchy.list.facet.standard', 'Standard') }],
    active:    [{ value: 'Y', label: t('common.boolean.active', 'Active') }, { value: 'N', label: t('common.boolean.inactive', 'Inactive') }],
  }), [t]);

  const facetsActive = facets.isDefault.length + facets.active.length > 0;
  const anyFilterActive = facetsActive || filter.trim().length > 0;
  const clearAll = () => { setFilter(''); setFacets({ isDefault: [], active: [] }); };

  const filtered = useMemo(() => {
    // OR within a facet, AND across facets.
    const matchesFacets = (r: HierarchyRow) =>
      (facets.isDefault.length === 0 || facets.isDefault.includes(r.is_default)) &&
      (facets.active.length    === 0 || facets.active.includes(r.is_active));
    const base = (!filter.trim()
      ? rows
      : rows.filter(r => {
          const q = filter.toLowerCase();
          return r.internal_code.toLowerCase().includes(q) ||
            r.display_text.toLowerCase().includes(q);
        })).filter(r => udcGrid.matchRow(r as unknown as Record<string, unknown>)).filter(matchesFacets);
    return sort.sorted(base, (a, b, key) => {
      if (udcGrid.isUdcKey(key)) return udcGrid.compare(a as unknown as Record<string, unknown>, b as unknown as Record<string, unknown>, key);
      if (key === 'step_count') return a.step_count - b.step_count;
      return compareStrings(a[key], b[key]);
    });
  }, [rows, filter, facets, sort, udcGrid]);

  const hasRelated = (id: number) =>
    (stepsByHier.get(id)?.length ?? 0) > 0 || (jobsByHier.get(id)?.length ?? 0) > 0
    || (rows.find(r => r.approval_hierarchy_id === id)?.tl.length ?? 0) > 0;

  const buildRelatedGroups = (id: number): RelatedChildGroup[] => [
    {
      key: 'steps',
      table: 'csm_approval_hierarchy_step', pkColumn: 'approval_hierarchy_step_id',
      title: t('approvals.hierarchy.related.steps', 'Approval Steps'),
      items: (stepsByHier.get(id) ?? []).map(s => ({
        id: s.step_id, label: s.label, title: s.email || undefined, badges: [String(s.sequence_order)],
      })),
    },
    {
      key: 'jobs',
      table: 'csm_job_description', pkColumn: 'job_description_id',
      title: t('approvals.hierarchy.related.jobs', 'Job Descriptions'),
      items: (jobsByHier.get(id) ?? []).map(j => ({ id: j.job_id, label: j.name, title: j.code })),
    },
    buildTranslationsGroup(rows.find(r => r.approval_hierarchy_id === id)?.tl, t, activeLocale),
  ];

  return (
    <div className="flex h-full flex-col">
      <div className="flex min-h-0 flex-1 flex-col gap-[18px] overflow-auto p-6">
        <PageHeader
          title={t('approval_hierarchy.list.title', 'Approval Hierarchies')}
          description={t('approval_hierarchy.list.description', 'Reusable approver chains that job descriptions reference for sign-off.')}
          actions={
            <>
              <button type="button" onClick={() => void reload()} className="btn-secondary flex items-center gap-1.5">
                <RefreshCw className="h-3.5 w-3.5" /> {t('common.refresh')}
              </button>
              {canAdd && (<button type="button" onClick={() => setCreating(true)} className="btn-primary flex items-center gap-1.5">
                <Plus className="h-3.5 w-3.5" /> {t('approval_hierarchy.list.new_hierarchy')}
              </button>)}
            </>
          }
        />

        {error && (
          <ErrorBanner
            message={t('approval_hierarchy.list.load_failed', "Couldn't load approval hierarchies.")}
            detail={error}
            retry_label={t('common.retry', 'Retry')}
            on_retry={() => void reload()}
            details_label={t('common.details', 'Details')}
          />
        )}

        <ContentCard className="flex min-h-0 flex-1 flex-col">
          <div className="flex shrink-0 flex-wrap items-center gap-2 border-b border-csm-line bg-white px-3 py-2">
            <div className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-csm-ink4" aria-hidden />
              <input
                type="text" value={filter} onChange={e => setFilter(e.target.value)}
                placeholder={t('approval_hierarchy.list.search_placeholder')}
                className="h-8 w-72 rounded-csm-control border border-csm-border pl-8 pr-2 text-csm-md focus:border-primary focus:outline-none"
              />
            </div>
            <FacetFilter label={t('approval_hierarchy.list.columns.default', 'Default')} options={facetOptions.isDefault} selected={facets.isDefault} onChange={v => setFacets(f => ({ ...f, isDefault: v }))} />
            <FacetFilter label={t('approval_hierarchy.list.columns.active', 'Active')} options={facetOptions.active} selected={facets.active} onChange={v => setFacets(f => ({ ...f, active: v }))} />
            <UdcFacets grid={udcGrid} />
            <ClearFiltersButton active={anyFilterActive} onClear={clearAll} label={t('common.clear_filters', 'Clear filters')} />
            <div className="ml-auto flex items-center gap-2">
              {busy && <span className="text-csm-xs text-csm-muted">{t('common.loading')}</span>}
              <PersonalizationToggle />
            </div>
          </div>
          {isPersonalizing && (
            <div className="shrink-0 border-b border-primary/20 bg-primary/5 px-3 py-1 text-csm-xs text-primary">
              {t('approval_hierarchy.list.personalization_banner', 'Personalization mode — configure columns, then click the icon to save and exit.')}
            </div>
          )}

          <div className="min-h-0 flex-1 overflow-auto">
        <PersonalizedGrid componentKey="grid" columns={[...GRID_COLS, ...udcGrid.columns]}>
          {(visibleCols, widths) => (
            <table className={GRID_TABLE_CLASS}>
              <thead className="sticky top-0 z-10 bg-csm-surface-alt text-csm-xs uppercase tracking-wider text-csm-ink-soft">
                <tr>
                  <th className="w-7 border-b border-csm-border px-1 py-1.5" title={t('approvals.hierarchy.related.header', 'Related approval steps & job descriptions')}></th>
                  {visibleCols.map(col => (
                    <SortableHeader<HierarchySortKey> key={col.key}
                      sortKey={col.key as HierarchySortKey} label={col.header}
                      currentKey={sort.sortKey} currentDir={sort.sortDir} onSort={sort.setSort}
                      style={widths[col.key] ? { width: widths[col.key], minWidth: widths[col.key] } : undefined} />
                  ))}
                  <th className="border-b border-csm-border px-3 py-1.5 text-left" style={{ width: '1%' }}>{t('approval_hierarchy.list.columns.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(h => (
                  <Fragment key={h.approval_hierarchy_id}>
                  <tr className="hover:bg-csm-surface-alt">
                    <td className="border-b border-csm-border-soft px-1 py-1.5 text-center align-middle" onClick={(e) => e.stopPropagation()}>
                      <RelatedExpandButton
                        open={detail.isOpen(h.approval_hierarchy_id)}
                        hasChildren={hasRelated(h.approval_hierarchy_id)}
                        onToggle={() => detail.toggle(h.approval_hierarchy_id)}
                        expandLabel={t('approvals.hierarchy.related.expand_aria', 'Show related records')}
                        collapseLabel={t('approvals.hierarchy.related.collapse_aria', 'Hide related')}
                        tooltip={t('approvals.hierarchy.related.tooltip', 'Show related records')}
                      />
                    </td>
                    {visibleCols.map(col => (
                      <td key={col.key} className={`border-b border-csm-border-soft px-3 py-1.5${col.key === 'step_count' ? ' csm-num' : ''}`}
                          style={widths[col.key] ? { width: widths[col.key] } : undefined}>
                        {col.key === 'internal_code' && <span className="font-mono text-csm-sm">{h.internal_code}</span>}
                        {col.key === 'display_text'  && h.display_text}
                        {col.key === 'step_count'    && h.step_count}
                        {col.key === 'is_default'    && <YesNoBadge value={h.is_default} yesLabel={t('approval_hierarchy.list.facet.default', 'Default')} noLabel={t('approval_hierarchy.list.facet.standard', 'Standard')} />}
                        {col.key === 'is_active'     && <YesNoBadge value={h.is_active}  yesLabel={t('common.boolean.active', 'Active')}  noLabel={t('common.boolean.inactive', 'Inactive')} />}
                        {udcGrid.isUdcKey(col.key)   && udcGrid.renderCell(h as unknown as Record<string, unknown>, col.key)}
                      </td>
                    ))}
                    <td className="border-b border-csm-border-soft px-3 py-1.5 text-right whitespace-nowrap">
                      {canChange && (<button onClick={(e) => { e.stopPropagation(); setEditing(h); }} className="mr-1 inline-block min-w-[3.25rem] rounded border border-csm-border bg-white px-2 py-0.5 text-csm-xs hover:bg-csm-border-soft">{t('approval_hierarchy.list.actions.edit')}</button>)}
                      {canDelete && (<button onClick={(e) => { e.stopPropagation(); setDeleteTarget(h); }}
                        disabled={h.is_default === 'Y'}
                        title={h.is_default === 'Y' ? t('approval_hierarchy.list.actions.delete_disabled_tooltip') : t('approval_hierarchy.list.actions.delete_tooltip')}
                        className="inline-block min-w-[3.75rem] rounded border border-csm-danger-border bg-csm-danger-soft px-2 py-0.5 text-csm-xs text-csm-danger hover:bg-csm-danger-soft disabled:cursor-not-allowed disabled:opacity-40"
                      >{t('approval_hierarchy.list.actions.delete')}</button>)}
                    </td>
                  </tr>
                  {detail.isOpen(h.approval_hierarchy_id) && (
                    <tr>
                      <td colSpan={visibleCols.length + 2} className="border-b border-csm-border bg-csm-surface-alt/40 px-3 py-3" onClick={(e) => e.stopPropagation()}>
                        <RelatedChildrenPanel groups={buildRelatedGroups(h.approval_hierarchy_id)} session={session} />
                      </td>
                    </tr>
                  )}
                  </Fragment>
                ))}
                {filtered.length === 0 && !busy && (
                  <tr><td colSpan={visibleCols.length + 2} className="py-12 text-center">
                    <EmptyState
                      variant={anyFilterActive ? 'no-match' : 'no-data'}
                      title={t('approval_hierarchy.list.empty')}
                      action={
                        canAdd ? <button onClick={() => setCreating(true)} className="mt-2 rounded bg-primary px-3 py-1.5 text-csm-sm text-white hover:bg-primary-hover">
                          {t('approval_hierarchy.list.new_hierarchy_plus', '+ New Hierarchy')}
                        </button> : undefined
                      }
                    />
                  </td></tr>
                )}
              </tbody>
            </table>
          )}
        </PersonalizedGrid>
          </div>
        </ContentCard>
      </div>

      {(editing || creating) && (
        <HierarchyEditModal
          session={session}
          row={editing}
          onClose={() => { setEditing(null); setCreating(false); }}
          onSaved={() => { setEditing(null); setCreating(false); void reload(); }}
        />
      )}

      {deleteTarget && (
        <ConfirmModal
          title={t('approval_hierarchy.list.delete_confirm_title', 'Delete Hierarchy?')}
          message={t('approval_hierarchy.list.delete_confirm_message', 'Delete "{{name}}"? This cannot be undone.', { name: deleteTarget.display_text || deleteTarget.internal_code })}
          onConfirm={async () => { const ok = await handleDelete(deleteTarget); if (ok) setDeleteTarget(null); }}
          onCancel={() => setDeleteTarget(null)}
        />
      )}
    </div>
  );
}

// =============================================================================
// EDIT DRAWER
// =============================================================================

interface HierarchyEditModalProps {
  session: AuthSession;
  row: HierarchyRow | null;
  onClose: () => void;
  onSaved: () => void;
}

function HierarchyEditModal({ session, row, onClose, onSaved }: HierarchyEditModalProps) {
  const { t } = useTranslation();
  const { isPersonalizing } = usePersonalization();
  const CK = 'drawer';
  const isCreate = row === null;

  const initialValues = {
    internalCode: row?.internal_code ?? '',
    isActive: row ? row.is_active === 'Y' : true,
    isDefault: row ? row.is_default === 'Y' : false,
    requiresManager: row ? row.requires_manager_approval === 'Y' : false,
  };
  const { values: form, setValues: setForm, isDirty, reset } = useDirtyForm(initialValues);
  const udcForm = useUdcFormFields('csm_approval_hierarchy', 'CSM.HIERARCHY.LIST', { pkColumn: 'approval_hierarchy_id', pk: row?.approval_hierarchy_id });
  const hierCoreLayout = useMemo(() => [...HIER_CORE_LAYOUT, ...udcForm.coreLayout], [udcForm.coreLayout]);
  const hierFieldDefs = useMemo(() => [...HIER_FIELD_DEFS, ...udcForm.fieldDefs], [udcForm.fieldDefs]);
  const { layout } = useUldLayout('CSM.HIERARCHY.MODAL', hierCoreLayout);
  const [translations, setTranslations] = useState<TLRow[]>([]);
  const [steps, setSteps]   = useState<StepRow[]>([]);
  const [users, setUsers]   = useState<UserOpt[]>([]);
  const [busy, setBusy]     = useState(false);
  const [error, setError]   = useState<string | null>(null);
  const [showDiscard, setShowDiscard] = useState(false);
  const [internalCodeError, setInternalCodeError] = useState<string | null>(null);

  const draggingIdRef = useRef<number | null>(null);
  const [draggingId, setDraggingId] = useState<number | null>(null);
  const [dropTarget, setDropTarget] = useState<{ id: number; pos: 'above' | 'below' } | null>(null);

  useEffect(() => {
    reset(initialValues);
    setInternalCodeError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [row?.approval_hierarchy_id, isCreate]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const { data: uData } = await apiDb
        .from('csm_user_master')
        .select('user_id, display_name, email')
        .eq('is_active', 'Y')
        .order('display_name');
      if (!cancelled) setUsers((uData ?? []) as UserOpt[]);

      if (isCreate || !row) {
        setTranslations([{ locale_code: 'EN_US', display_text: '', description: '' }]);
        setSteps([]);
        return;
      }
      const { data: tlData } = await apiDb
        .from('csm_approval_hierarchy_tl')
        .select('locale_code, display_text, description')
        .eq('approval_hierarchy_id', row.approval_hierarchy_id);
      if (!cancelled) {
        const tls: TLRow[] = (tlData ?? []).map((t: TlRow) => ({
          locale_code: t.locale_code, display_text: t.display_text ?? '', description: t.description ?? '',
        }));
        setTranslations(tls.length > 0 ? tls : [{ locale_code: 'EN_US', display_text: '', description: '' }]);
      }

      const { data: sData } = await apiDb
        .from('csm_approval_hierarchy_step')
        .select(`
          approval_hierarchy_step_id, user_id, sequence_order, is_active,
          csm_user_master:user_id ( display_name, email )
        `)
        .eq('approval_hierarchy_id', row.approval_hierarchy_id)
        .order('sequence_order');
      if (!cancelled) {
        type RawStep = {
          approval_hierarchy_step_id: number;
          user_id: number;
          sequence_order: number;
          is_active: 'Y' | 'N';
          csm_user_master?: { display_name?: string; email?: string } | null;
        };
        const list: StepRow[] = (sData ?? []).map((s: RawStep) => ({
          approval_hierarchy_step_id: s.approval_hierarchy_step_id,
          user_id: s.user_id,
          user_label: s.csm_user_master?.display_name ?? s.csm_user_master?.email ?? `#${s.user_id}`,
          sequence_order: s.sequence_order,
          is_active: s.is_active,
        }));
        setSteps(list);
      }
    })();
    return () => { cancelled = true; };
  }, [isCreate, row?.approval_hierarchy_id]); // eslint-disable-line

  const handleClose = () => {
    if (isDirty) { setShowDiscard(true); return; }
    onClose();
  };

  const addStep = (code: string | null) => {
    if (!code) return;
    const userId = Number(code);
    const u = users.find(uu => uu.user_id === userId);
    if (!u) return;
    if (steps.some(s => s.user_id === u.user_id)) {
      setError(t('approval_hierarchy.drawer.chain.already_in_chain')); return;
    }
    setSteps(prev => [...prev, {
      approval_hierarchy_step_id: -Math.floor(Math.random() * 1e9),
      user_id: u.user_id,
      user_label: u.display_name || u.email,
      sequence_order: prev.length === 0 ? SORT_STEP : Math.max(...prev.map(p => p.sequence_order)) + SORT_STEP,
      is_active: 'Y',
    }]);
    setError(null);
  };
  const removeStep = (id: number) => setSteps(prev => prev.filter(s => s.approval_hierarchy_step_id !== id));

  // DnD
  const onDragStart = (e: React.DragEvent<HTMLTableRowElement>, id: number) => {
    draggingIdRef.current = id; setDraggingId(id); e.dataTransfer.effectAllowed = 'move';
  };
  const onDragOver = (e: React.DragEvent<HTMLTableRowElement>, id: number) => {
    const src = draggingIdRef.current;
    if (src == null || src === id) return;
    e.preventDefault();
    const rect = e.currentTarget.getBoundingClientRect();
    const y = e.clientY - rect.top;
    const pos: 'above' | 'below' = y < rect.height / 2 ? 'above' : 'below';
    setDropTarget(prev => prev?.id === id && prev.pos === pos ? prev : { id, pos });
  };
  const onDragLeave = (id: number) => setDropTarget(prev => (prev?.id === id ? null : prev));
  const onDragEnd = () => { draggingIdRef.current = null; setDraggingId(null); setDropTarget(null); };
  const onDrop = (e: React.DragEvent<HTMLTableRowElement>) => {
    e.preventDefault();
    const src = draggingIdRef.current; const tgt = dropTarget;
    onDragEnd();
    if (src == null || !tgt) return;
    setSteps(prev => {
      const ordered = [...prev].sort((a, b) => a.sequence_order - b.sequence_order);
      const fromIdx = ordered.findIndex(a => a.approval_hierarchy_step_id === src);
      if (fromIdx === -1) return prev;
      const moving = ordered[fromIdx];
      const without = ordered.filter((_, i) => i !== fromIdx);
      let toIdx = without.findIndex(a => a.approval_hierarchy_step_id === tgt.id);
      if (tgt.pos === 'below') toIdx += 1;
      const next = [...without.slice(0, toIdx), moving, ...without.slice(toIdx)];
      return next.map((a, i) => ({ ...a, sequence_order: (i + 1) * SORT_STEP }));
    });
  };

  const dropClass = (id: number) => {
    if (!dropTarget || dropTarget.id !== id) return '';
    return dropTarget.pos === 'above' ? 'border-t-2 border-primary' : 'border-b-2 border-primary';
  };

  const save = async () => {
    setBusy(true); setError(null);
    try {
      let hierId = row?.approval_hierarchy_id ?? null;

      if (isCreate) {
        const { row_id, error: e } = await routedInsert(session, 'csm_approval_hierarchy', {
          internal_code: form.internalCode,
          is_default: form.isDefault ? 'Y' : 'N',
          is_active:  form.isActive  ? 'Y' : 'N',
          requires_manager_approval: form.requiresManager ? 'Y' : 'N',
        });
        if (e) throw e;
        hierId = row_id;
      } else {
        const { error: e } = await routedUpdate(session, 'csm_approval_hierarchy', 'approval_hierarchy_id', row?.approval_hierarchy_id, {
          is_default: form.isDefault ? 'Y' : 'N',
          is_active:  form.isActive  ? 'Y' : 'N',
          requires_manager_approval: form.requiresManager ? 'Y' : 'N',
        });
        if (e) throw e;
      }

      // Translations
      for (const t of translations) {
        if (!t.locale_code || !t.display_text) continue;
        const { error: e } = await apiDb
          .from('csm_approval_hierarchy_tl')
          .upsert({
            approval_hierarchy_id: hierId,
            locale_code: t.locale_code,
            display_text: t.display_text,
            description: t.description || null,
          }, { onConflict: 'approval_hierarchy_id,locale_code' });
        if (e) throw e;
      }

      // Steps - delete + reinsert (snapshot strategy). Multi-row-by-filter has no
      // single-routine form (§2): resolve the existing step PKs, then route one
      // DELETE per row before re-inserting the new ordered set.
      const { data: existingSteps, error: sFindErr } = await apiDb
        .from('csm_approval_hierarchy_step')
        .select('approval_hierarchy_step_id')
        .eq('approval_hierarchy_id', hierId);
      if (sFindErr) throw sFindErr;
      for (const st of (existingSteps ?? []) as { approval_hierarchy_step_id: number }[]) {
        const { error: dErr } = await routedDelete(session, 'csm_approval_hierarchy_step', 'approval_hierarchy_step_id', st.approval_hierarchy_step_id);
        if (dErr) throw dErr;
      }

      if (steps.length > 0) {
        const ordered = [...steps].sort((a, b) => a.sequence_order - b.sequence_order);
        const inserts = ordered.map((s, i) => ({
          approval_hierarchy_id: hierId,
          user_id: s.user_id,
          sequence_order: (i + 1) * SORT_STEP,
          is_active: s.is_active,
        }));
        const { error: iErr } = await routedInsertMany(session, 'csm_approval_hierarchy_step', inserts);
        if (iErr) throw iErr;
      }

      onSaved();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const modalTitle = isCreate
    ? t('approval_hierarchy.drawer.create_title', 'Create Approval Hierarchy')
    : t('approval_hierarchy.drawer.edit_title', 'Edit Approval Hierarchy — {{code}}', { code: row?.internal_code ?? '' });

  return (
    <>
      <Modal
        title={modalTitle}
        onClose={handleClose}
        width="2xl"
        headerAction={<>{session.effective_user.page_access.length > 0 && session.effective_user.page_access.every(p => p.can_view && p.can_change) && null}<PersonalizationToggle /></>}
        footer={
          <>
            <button onClick={handleClose} className="rounded border border-csm-border bg-white px-3 py-1 text-csm-sm hover:bg-csm-border-soft">{t('common.cancel')}</button>
            <button onClick={() => void save()} disabled={busy || (isCreate && !form.internalCode)}
                    className="rounded border border-primary bg-primary px-3 py-1 text-csm-sm text-white hover:bg-primary-hover disabled:opacity-60">
              {busy ? t('common.saving') : t('common.save')}
            </button>
          </>
        }
      >
        {isPersonalizing && (
          <div className="-mx-4 -mt-4 mb-4 border-b border-primary/20 bg-primary/5 px-3 py-1 text-csm-xs text-primary">
            {t('approval_hierarchy.drawer.personalization_banner', 'Toggle fields — hide · read-only · required. Click icon to save.')}
          </div>
        )}
        {/* min-h-0 lets this flex child shrink below its natural content height
            so the sticky footer stays visible even with a tall body. Without
            it, the default min-height: auto pushes the footer off-screen. */}
        <div className="mb-3">
          <FieldRenderer
            fields={hierFieldDefs}
            layout={layout}
            columns={3}
            pageCode="CSM.HIERARCHY.MODAL"
            session={session}
            render={(field) => {
              if (field.key === 'internal_code') return (
                <PersonalizedField componentKey={CK} fieldKey="internal_code" label={t('approval_hierarchy.drawer.field.internal_code')}>
                  <div>
                    <label className="mb-1 block text-csm-sm font-medium text-csm-ink-soft">{t('approval_hierarchy.drawer.field.internal_code')}</label>
                    <input
                      type="text" value={form.internalCode} disabled={!isCreate}
                      onChange={e => setForm(f => ({ ...f, internalCode: e.target.value.toUpperCase() }))}
                      onBlur={() => {
                        if (isCreate && form.internalCode && !/^[A-Z][A-Z0-9_.]*$/.test(form.internalCode)) {
                          setInternalCodeError(t('approval_hierarchy.drawer.field.internal_code_error', 'Must start with a letter — uppercase letters, numbers, dots only.'));
                        } else {
                          setInternalCodeError(null);
                        }
                      }}
                      placeholder={t('approval_hierarchy.drawer.field.internal_code_placeholder')}
                      className="h-8 w-full rounded border border-csm-border px-2 font-mono text-csm-sm focus:border-csm-watermark focus:outline-none disabled:bg-csm-surface-alt disabled:text-csm-muted"
                    />
                    {internalCodeError
                      ? <p className="text-csm-xs text-red-600 mt-1">{internalCodeError}</p>
                      : isCreate && <p className="text-csm-xs text-csm-ink-soft mt-1">{t('approval_hierarchy.drawer.field.internal_code_hint', 'E.g. CSM.MANAGER — uppercase, dots only')}</p>
                    }
                  </div>
                </PersonalizedField>
              );
              if (field.key === 'is_active') return (
                <PersonalizedField componentKey={CK} fieldKey="is_active" label={t('approval_hierarchy.drawer.field.active_checkbox')}>
                  <div className="flex items-end h-full pb-1">
                    <label className="flex items-center gap-2 text-csm-sm text-csm-ink-soft">
                      <input type="checkbox" checked={form.isActive} onChange={e => setForm(f => ({ ...f, isActive: e.target.checked }))} className="h-4 w-4 accent-gray-700" />
                      {t('approval_hierarchy.drawer.field.active_checkbox')}
                    </label>
                  </div>
                </PersonalizedField>
              );
              if (field.key === 'is_default') return (
                <PersonalizedField componentKey={CK} fieldKey="is_default" label={t('approval_hierarchy.drawer.field.use_as_default')}>
                  <div className="flex items-end h-full pb-1">
                    <label className="flex items-center gap-2 text-csm-sm text-csm-ink-soft">
                      <input type="checkbox" checked={form.isDefault} onChange={e => setForm(f => ({ ...f, isDefault: e.target.checked }))} className="h-4 w-4 accent-gray-700" />
                      {t('approval_hierarchy.drawer.field.use_as_default')}
                    </label>
                  </div>
                </PersonalizedField>
              );
              if (udcForm.isUdcKey(field.key)) return (
                <PersonalizedField componentKey={CK} fieldKey={field.key} label={field.label}
                  readonlyChildren={udcForm.renderControl(field.key, { disabled: true })}>
                  {udcForm.renderControl(field.key)}
                </PersonalizedField>
              );
              return null;
            }}
          />
        </div>
        {form.isDefault && (
          <div className="mb-3 rounded border border-csm-info-border bg-csm-info-soft px-2 py-1 text-csm-xs text-csm-info">
            {t('approval_hierarchy.drawer.hints.default_warning')}
          </div>
        )}

        <PersonalizedField componentKey={CK} fieldKey="requires_manager_approval" label={t('approval_hierarchy.drawer.field.requires_manager_approval')}>
          <div className="mb-3 rounded border border-csm-border bg-csm-surface-alt px-3 py-2">
            <label className="flex items-center gap-2 text-csm-sm text-csm-ink">
              <input type="checkbox" checked={form.requiresManager} onChange={e => setForm(f => ({ ...f, requiresManager: e.target.checked }))} className="h-4 w-4 accent-gray-700" />
              <span className="font-medium">{t('approval_hierarchy.drawer.field.requires_manager_approval')}</span>
            </label>
            <div className="mt-1 text-csm-xs text-csm-ink-soft">
              {t('approval_hierarchy.drawer.hints.requires_manager')}
            </div>
          </div>
        </PersonalizedField>

        {/* Translations */}
        <PersonalizedField componentKey={CK} fieldKey="translations" label={t('approval_hierarchy.drawer.translations.label')}>
          <div className="mb-2 flex items-center justify-between">
            <label className="text-csm-sm font-medium text-csm-ink-soft">{t('approval_hierarchy.drawer.translations.label')}</label>
            <button type="button" onClick={() => setTranslations(prev => [...prev, { locale_code: '', display_text: '', description: '' }])}
                    className="text-csm-xs text-csm-ink-soft hover:underline">{t('approval_hierarchy.drawer.translations.add_locale')}</button>
          </div>
          <div className="mb-4 overflow-hidden rounded border border-csm-border">
            <table className="w-full text-csm-sm">
              <thead className="bg-csm-surface-alt text-csm-xs uppercase tracking-wider text-csm-ink-soft">
                <tr>
                  <th className="px-2 py-1 text-left">{t('approval_hierarchy.drawer.translations.columns.locale')}</th>
                  <th className="px-2 py-1 text-left">{t('approval_hierarchy.drawer.translations.columns.display_text')}</th>
                  <th className="px-2 py-1 text-left">{t('approval_hierarchy.drawer.translations.columns.description')}</th>
                  <th className="w-6"></th>
                </tr>
              </thead>
              <tbody>
                {translations.map((tl, i) => (
                  <tr key={i} className="border-t border-csm-border-soft">
                    <td className="px-1 py-1">
                      <input value={tl.locale_code} onChange={e => {
                        const v = e.target.value.toUpperCase();
                        setTranslations(prev => prev.map((row, idx) => idx === i ? { ...row, locale_code: v } : row));
                      }} placeholder={t('approval_hierarchy.drawer.translations.locale_placeholder')} className="h-6 w-20 rounded border border-csm-border px-1 font-mono text-csm-xs" />
                    </td>
                    <td className="px-1 py-1">
                      <input value={tl.display_text} onChange={e => {
                        const v = e.target.value;
                        setTranslations(prev => prev.map((row, idx) => idx === i ? { ...row, display_text: v } : row));
                      }} className="h-6 w-full rounded border border-csm-border px-1 text-csm-sm" />
                    </td>
                    <td className="px-1 py-1">
                      <input value={tl.description} onChange={e => {
                        const v = e.target.value;
                        setTranslations(prev => prev.map((row, idx) => idx === i ? { ...row, description: v } : row));
                      }} className="h-6 w-full rounded border border-csm-border px-1 text-csm-sm" />
                    </td>
                    <td className="px-1 py-1 text-right">
                      <button type="button" onClick={() => setTranslations(prev => prev.filter((_, idx) => idx !== i))}
                              className="text-csm-watermark hover:text-csm-danger">x</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </PersonalizedField>

        {/* Approver chain */}
        <PersonalizedField componentKey={CK} fieldKey="approver_chain" label={t('approval_hierarchy.drawer.chain.label')}>
          <div className="mb-2">
            <label className="mb-1 block text-csm-sm font-medium text-csm-ink-soft">
              {t('approval_hierarchy.drawer.chain.label')}
            </label>
            <div className="w-72">
              <FilteredModalSingleSelect
                label={t('approval_hierarchy.drawer.chain.add_approver')}
                options={users
                  .filter(u => !steps.some(s => s.user_id === u.user_id))
                  .map(u => ({
                    internal_code: String(u.user_id),
                    display_text: u.display_name || u.email,
                    description: u.display_name ? u.email : undefined,
                  }))}
                selectedCode={null}
                onChange={(code) => addStep(code)}
                placeholder={t('approval_hierarchy.drawer.chain.add_approver_placeholder')}
                allowClear={false}
              />
            </div>
          </div>

          <div className="overflow-hidden rounded border border-csm-border">
            <table className="w-full text-csm-sm">
              <thead className="bg-csm-surface-alt text-csm-xs uppercase tracking-wider text-csm-ink-soft">
                <tr>
                  <th className="w-6 px-1 py-1"></th>
                  <th className="w-12 px-2 py-1 text-left">{t('approval_hierarchy.drawer.chain.columns.step')}</th>
                  <th className="px-2 py-1 text-left">{t('approval_hierarchy.drawer.chain.columns.approver')}</th>
                  <th className="w-6"></th>
                </tr>
              </thead>
              <tbody>
                {[...steps].sort((a, b) => a.sequence_order - b.sequence_order).map((s, idx) => (
                  <tr
                    key={s.approval_hierarchy_step_id}
                    draggable
                    onDragStart={(e) => onDragStart(e, s.approval_hierarchy_step_id)}
                    onDragOver={(e) => onDragOver(e, s.approval_hierarchy_step_id)}
                    onDragLeave={() => onDragLeave(s.approval_hierarchy_step_id)}
                    onDrop={onDrop}
                    onDragEnd={onDragEnd}
                    className={
                      'border-t border-csm-border-soft select-none ' +
                      (draggingId === s.approval_hierarchy_step_id ? 'opacity-40 ' : '') +
                      dropClass(s.approval_hierarchy_step_id)
                    }
                  >
                    <td className="px-1 py-1 text-center text-csm-watermark" style={{ cursor: 'grab' }}>::</td>
                    <td className="px-2 py-1">{idx + 1}</td>
                    <td className="px-2 py-1">{s.user_label}</td>
                    <td className="px-1 py-1 text-right">
                      <button type="button" onClick={() => removeStep(s.approval_hierarchy_step_id)}
                              className="text-csm-watermark hover:text-csm-danger">x</button>
                    </td>
                  </tr>
                ))}
                {steps.length === 0 && (
                  <tr><td colSpan={4} className="px-2 py-3 text-center italic text-csm-watermark">
                    {t('approval_hierarchy.drawer.chain.empty')}
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        </PersonalizedField>

        {error && (
          <div className="mt-3 rounded border border-csm-danger-border bg-csm-danger-soft px-2 py-1 text-csm-sm text-csm-danger">{error}</div>
        )}
      </Modal>

      {showDiscard && (
        <ConfirmModal
          title={t('common.discard_title', 'Discard changes?')}
          message={t('common.discard_message', 'You have unsaved changes that will be lost.')}
          confirmLabel={t('common.discard_confirm', 'Discard')}
          cancelLabel={t('common.discard_cancel', 'Keep editing')}
          danger={false}
          onConfirm={() => { reset(); onClose(); }}
          onCancel={() => setShowDiscard(false)}
        />
      )}
    </>
  );
}
